<template>
  <div class="group-dashboard">
    <div
      v-if="groupOptions.length"
      class="group-dashboard__topbar"
    >
      <div>
        <div class="text-sm text-secondary">Group dashboard</div>
        <h1 class="mt-1 text-3xl font-semibold md:text-5xl">{{ pageTitle }}</h1>
      </div>

      <div class="group-dashboard__filters">
        <Select
          v-if="groupOptions.length > 1"
          v-model="groupSelected"
          :options="groupOptions"
          value-key="id"
          label-key="name"
          searchable
          icon="building"
          placeholder="Select a Group"
          class="min-w-56 shrink-0"
        />
        <div
          v-else-if="groupOptions.length === 1"
          class="group-dashboard__single-filter"
        >
          <Icon
            type="building"
            class="mr-2"
          />
          {{ groupOptions[0].name }}
        </div>

        <Select
          v-model="manufacturerSelected"
          :options="manufacturerOptions"
          value-key="id"
          label-key="name"
          searchable
          clearable
          icon="industry"
          placeholder="All Manufacturers"
          class="min-w-56 shrink-0"
        />

        <DatePicker
          v-model="dateRange"
          mode="range"
          :presets="dateRangePresets"
          class="shrink-0"
        />
      </div>
    </div>

    <Empty
      v-if="groupOptions.length === 0"
      title="No group access"
      description="You have not been added to a dealership group."
      icon="building"
    />

    <div
      v-else
      class="group-dashboard__content"
    >
      <template v-if="dashboardRequest.isLoading.value && !hasDashboardData">
        <div class="grid gap-4 xl:grid-cols-3">
          <Card class="xl:col-span-2">
            <CardBody>
              <Skeleton
                height="18rem"
                border-radius="var(--rounded-2xl)"
              />
            </CardBody>
          </Card>
          <Card>
            <CardBody class="flex flex-col gap-3">
              <Skeleton
                v-for="index in 5"
                :key="index"
                height="3rem"
              />
            </CardBody>
          </Card>
        </div>
      </template>

      <template v-else>
        <Empty
          v-if="!hasDashboardData"
          title="No group dashboard data"
          description="Try a different group, manufacturer, or date range."
          icon="chart-mixed"
        />

        <template v-else>
          <GroupHeroPanel
            :totals="dashboardTotals"
            :best-dealership="bestDealership"
            :top-sales-person="topSalesPerson"
          />

          <GroupInsightStrip :insights="dashboardInsights" />

          <div class="grid gap-4 xl:grid-cols-5">
            <GroupLeaderboardCard
              class="xl:col-span-2"
              title="Top salespeople"
              :items="dashboardData.leaderboards.usersByDeals"
            />

            <Card
              :is-loading="dashboardRequest.isLoading.value"
              no-margin
              class="xl:col-span-3"
              :border="false"
              border-radius="2xl"
              pt-classes="group-dashboard-card"
            >
              <div class="group-dashboard-card__header">
                <div>
                  <div class="text-lg font-semibold">Conversion</div>
                  <div class="text-sm text-secondary">Dealership conversion ranking</div>
                </div>
              </div>
              <div class="group-dashboard-chart">
                <apexchart
                  type="bar"
                  height="320"
                  :options="conversionLeaderboardChart.options"
                  :series="conversionLeaderboardChart.series"
                ></apexchart>
              </div>
            </Card>
          </div>

          <div class="grid gap-4 xl:grid-cols-12">
            <div class="xl:col-span-9">
              <DealershipPerformanceGrid :dealerships="dashboardData.dealershipStats" />
            </div>
            <div class="xl:col-span-3">
              <GroupVehicleSpotlight :dealerships="dashboardData.dealershipStats" />
            </div>
          </div>
        </template>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import dayjs from 'dayjs'

// components
import Card from '@/components/card/Card.vue'
import CardBody from '@/components/card/CardBody.vue'
import DatePicker from '@/components/date-picker/DatePicker.vue'
import DealershipPerformanceGrid from '@/components/unique/dashboards/group/DealershipPerformanceGrid.vue'
import Empty from '@/components/empty/Empty.vue'
import GroupHeroPanel from '@/components/unique/dashboards/group/GroupHeroPanel.vue'
import GroupInsightStrip from '@/components/unique/dashboards/group/GroupInsightStrip.vue'
import GroupLeaderboardCard from '@/components/unique/dashboards/group/GroupLeaderboardCard.vue'
import GroupVehicleSpotlight from '@/components/unique/dashboards/group/GroupVehicleSpotlight.vue'
import Icon from '@/components/icon/Icon.vue'
import Select from '@/components/forms/Select.vue'
import Skeleton from '@/components/skeleton/Skeleton.vue'

// composables
import useApiRequest from '@/composables/useApiRequest'

// stores
import { useAccessStore } from '@/stores/AccessStore'

// types
import type { DateRange } from '@/types/components/dateTime'
import type {
  GroupDashboardApiResponse,
  GroupDashboardData,
  GroupDashboardDealership,
  GroupDashboardInsight,
  GroupDashboardSalesPerson,
  GroupDashboardTotals
} from '@/types/domain/groupDashboard'

// utilities
import apexChartDefaults from '@/components/charts/apexChartDefaults'
import { datePresets } from '@/utilities/date-time'
import { formatCurrency, formatNumber } from '@/utilities/number'
import { alertToast } from '@/utilities/notification'

/** Empty data shape used before the first API response is available. */
const defaultDashboardData: GroupDashboardData = {
  group: null,
  dealershipStats: [],
  leaderboards: {
    usersByDeals: [],
    dealershipsByAppointmentToDealConversion: []
  }
}

/** Store that contains the user's allowed group and manufacturer access. */
const accessStore = useAccessStore()

/** API request state and sender for the group dashboard endpoint. */
const dashboardRequest = useApiRequest()

/** Local storage keys for group dashboard filters. */
const groupDashboardFilterKeys = {
  group: 'group-dashboard-group-selected',
  manufacturer: 'group-dashboard-manufacturer-selected',
  dateRange: 'group-dashboard-date-range'
}

/** Available groups from the user's access model. */
const groupOptions = computed(() => accessStore.accessAllowed.groups)

/** Current selected group ID for the dashboard request. */
const groupSelected = ref<number | undefined>(
  storedNumber(groupDashboardFilterKeys.group) ?? accessStore.accessSelected.groups[0] ?? groupOptions.value[0]?.id
)

/** Current selected manufacturer filter for the dashboard request. */
const manufacturerSelected = ref<number | undefined>(
  storedNumber(groupDashboardFilterKeys.manufacturer) ?? accessStore.accessSelected.manufacturers[0]
)

/** Date range selected in the dashboard header. */
const dateRange = ref<DateRange>(
  storedDateRange(groupDashboardFilterKeys.dateRange, {
    start: dayjs().subtract(30, 'day').format('YYYY-MM-DD'),
    end: dayjs().startOf('day').format('YYYY-MM-DD')
  })
)

/** Quick date ranges reused from other dashboards. */
const dateRangePresets = [datePresets.last30Days, datePresets.thisWeek, datePresets.thisMonth, datePresets.lastMonth]

/** Raw dashboard data returned by the endpoint. */
const dashboardData = ref<GroupDashboardData>(defaultDashboardData)

/** Manufacturer options filtered by the selected group where the access model allows it. */
const manufacturerOptions = computed(() => {
  const selectedGroup = groupOptions.value.find(group => group.id === groupSelected.value)

  // Only filter manufacturers when the selected group exposes manufacturer IDs.
  if (selectedGroup?.manufacturers?.length) {
    return accessStore.accessAllowed.manufacturers.filter(manufacturer =>
      selectedGroup.manufacturers?.includes(manufacturer.id)
    )
  }

  return accessStore.accessAllowed.manufacturers
})

/** Page title from the loaded group, with a stable fallback before data loads. */
const pageTitle = computed(() => dashboardData.value.group?.name ?? 'Group Dashboard')

/** Whether the endpoint has returned dealership rows for the selected filters. */
const hasDashboardData = computed(() => dashboardData.value.dealershipStats.length > 0)

/** Derived group-level totals used by the hero, insights, and charts. */
const dashboardTotals = computed<GroupDashboardTotals>(() => {
  const totals = dashboardData.value.dealershipStats.reduce(
    (carry, item) => {
      carry.calls += item.stats.calls
      carry.sms += item.stats.sms
      carry.emails += item.stats.emails
      carry.callsAnswered += item.stats.callsAnswered
      carry.appointmentsBooked += item.stats.appointmentsBooked
      carry.appointmentsOccurring += item.stats.appointmentsOccurring
      carry.deals += item.stats.deals
      carry.totalRevenue += item.stats.totalRevenue
      return carry
    },
    {
      calls: 0,
      sms: 0,
      emails: 0,
      callsAnswered: 0,
      appointmentsBooked: 0,
      appointmentsOccurring: 0,
      deals: 0,
      totalRevenue: 0
    }
  )

  const totalCommunications = totals.calls + totals.sms + totals.emails

  return {
    ...totals,
    totalCommunications,
    dealershipCount: dashboardData.value.dealershipStats.length,
    answerRate: totals.calls > 0 ? totals.callsAnswered / totals.calls : 0,
    conversionRate: totals.appointmentsOccurring > 0 ? totals.deals / totals.appointmentsOccurring : 0,
    averageRevenuePerDeal: totals.deals > 0 ? totals.totalRevenue / totals.deals : 0
  }
})

/** Best dealership by revenue, used for the hero spotlight. */
const bestDealership = computed<GroupDashboardDealership | null>(() => {
  const dealerships = [...dashboardData.value.dealershipStats]

  // No winner can be selected when the endpoint returns no rows.
  if (dealerships.length === 0) {
    return null
  }

  return dealerships.sort((a, b) => b.stats.totalRevenue - a.stats.totalRevenue)[0]
})

/** Top salesperson by deals, with revenue as the tie-breaker. */
const topSalesPerson = computed<GroupDashboardSalesPerson | null>(() => {
  const leaders = [...dashboardData.value.leaderboards.usersByDeals]

  // No top salesperson exists before sales leaderboard data is available.
  if (leaders.length === 0) {
    return null
  }

  return leaders.sort((a, b) => b.deals - a.deals || b.totalRevenue - a.totalRevenue)[0]
})

/** Derived insight cards shown below the hero. */
const dashboardInsights = computed<GroupDashboardInsight[]>(() => {
  const strongestConversion = dashboardData.value.leaderboards.dealershipsByAppointmentToDealConversion[0]
  const attentionDealership = [...dashboardData.value.leaderboards.dealershipsByAppointmentToDealConversion]
    .filter(item => item.appointmentsOccurring > 0)
    .sort((a, b) => a.conversionRate - b.conversionRate)[0]

  return [
    {
      label: 'Strongest conversion',
      value: strongestConversion?.dealership.name ?? 'N/A',
      shortValue: strongestConversion ? formatPercent(strongestConversion.conversionRate) : undefined,
      helper: strongestConversion
        ? `${formatPercent(strongestConversion.conversionRate)} from ${strongestConversion.appointmentsOccurring} appointments`
        : 'No appointment data yet',
      icon: 'chart-line-up',
      color: 'success'
    },
    {
      label: 'Revenue engine',
      value: bestDealership.value?.dealership.name ?? 'N/A',
      shortValue: formatCurrency(bestDealership.value?.stats.totalRevenue ?? 0, 'GBP', 0) ?? '£0',
      helper: `${formatCurrency(bestDealership.value?.stats.totalRevenue ?? 0, 'GBP', 0) ?? '£0'} generated`,
      icon: 'sterling-sign',
      color: 'brand'
    },
    {
      label: 'Needs attention',
      value: attentionDealership?.dealership.name ?? 'N/A',
      shortValue: attentionDealership ? formatPercent(attentionDealership.conversionRate) : undefined,
      helper: attentionDealership
        ? `${formatPercent(attentionDealership.conversionRate)} conversion`
        : 'No underperforming dealership found',
      icon: 'sparkles',
      color: 'warning'
    }
  ]
})

/** Horizontal leaderboard chart for dealership conversion rate. */
const conversionLeaderboardChart = computed(() => ({
  options: {
    ...apexChartDefaults.barChart.chartOptions,
    chart: {
      ...apexChartDefaults.barChart.chartOptions.chart,
      type: 'bar'
    },
    colors: ['var(--utility-purple-500)'],
    plotOptions: {
      bar: {
        horizontal: true,
        borderRadius: 6,
        barHeight: '58%'
      }
    },
    xaxis: {
      categories: dashboardData.value.leaderboards.dealershipsByAppointmentToDealConversion.map(
        item => item.dealership.name
      ),
      labels: {
        formatter: (value: string) => `${formatNumber(Number(value), 'normal', 0) ?? '0'}%`,
        style: {
          colors: 'var(--text-secondary)'
        }
      }
    },
    yaxis: {
      labels: {
        style: {
          colors: 'var(--text-secondary)'
        }
      }
    },
    tooltip: {
      y: {
        formatter: (value: number) => `${formatNumber(value, 'normal', 1) ?? '0'}%`
      }
    }
  },
  series: [
    {
      name: 'Conversion',
      data: dashboardData.value.leaderboards.dealershipsByAppointmentToDealConversion.map(item =>
        Number((item.conversionRate * 100).toFixed(2))
      )
    }
  ]
}))

watch(
  groupOptions,
  groups => {
    const selectedGroupIsAllowed = groups.some(group => group.id === groupSelected.value)

    // Replace stale persisted group filters when access no longer includes them.
    if (groupSelected.value != null && !selectedGroupIsAllowed) {
      groupSelected.value = groups[0]?.id
      return
    }

    // Select the first available group when access arrives after the component mounts.
    if (groupSelected.value == null && groups.length > 0) {
      groupSelected.value = groups[0].id
    }
  },
  { immediate: true }
)

watch(
  [groupSelected, manufacturerOptions],
  () => {
    const selectedManufacturerIsAllowed = manufacturerOptions.value.some(
      manufacturer => manufacturer.id === manufacturerSelected.value
    )

    // Clear stale manufacturer filters when switching to a group that does not include them.
    if (manufacturerSelected.value && !selectedManufacturerIsAllowed) {
      manufacturerSelected.value = undefined
    }
  },
  { immediate: true }
)

watch(
  [groupSelected, manufacturerSelected, dateRange],
  () => {
    persistDashboardFilters()
    fetchDashboardData()
  },
  { deep: true, immediate: true }
)

/**
 * Fetches group dashboard data for the selected filters.
 */
async function fetchDashboardData(): Promise<void> {
  if (groupSelected.value == null || !dateRange.value.start || !dateRange.value.end) {
    return
  }

  const params: Record<string, number | string> = {
    groupId: groupSelected.value,
    start: dayjs(dateRange.value.start).format('YYYY-MM-DD'),
    end: dayjs(dateRange.value.end).format('YYYY-MM-DD')
  }

  // Only include manufacturer when the optional filter has a value.
  if (manufacturerSelected.value) {
    params.manufacturer = manufacturerSelected.value
  }

  try {
    const response = (await dashboardRequest.send({
      endpoint: '/v1/dashboards/group',
      method: 'GET',
      params
    })) as GroupDashboardApiResponse

    if (response.success) {
      dashboardData.value = response.data
      persistSelectedAccess()
      return
    }

    alertToast('Failed to fetch group dashboard', response.message ?? 'Unknown error', 'error')
  } catch (error: unknown) {
    const message = error instanceof Error ? error.message : 'Unknown error'
    alertToast('Failed to fetch group dashboard', message, 'error')
  }
}

/**
 * Persists the selected group and manufacturer into the shared access selection.
 */
function persistSelectedAccess(): void {
  accessStore.setAccessSelected({
    ...accessStore.accessSelected,
    groups: groupSelected.value ? [groupSelected.value] : [],
    manufacturers: manufacturerSelected.value ? [manufacturerSelected.value] : []
  })
}

/**
 * Persists the current dashboard filters locally for this dashboard route.
 */
function persistDashboardFilters(): void {
  if (groupSelected.value) {
    localStorage.setItem(groupDashboardFilterKeys.group, String(groupSelected.value))
  }

  if (manufacturerSelected.value) {
    localStorage.setItem(groupDashboardFilterKeys.manufacturer, String(manufacturerSelected.value))
  } else {
    localStorage.removeItem(groupDashboardFilterKeys.manufacturer)
  }

  localStorage.setItem(groupDashboardFilterKeys.dateRange, JSON.stringify(dateRange.value))
}

/**
 * Gets a stored numeric filter value.
 * @param key - Local storage key.
 * @returns Numeric filter value when valid.
 */
function storedNumber(key: string): number | undefined {
  const value = Number(localStorage.getItem(key))

  if (Number.isNaN(value) || value === 0) {
    return undefined
  }

  return value
}

/**
 * Gets a stored date range when both dates are present.
 * Accepts the new DateRange object shape and migrates the legacy [start, end] array shape.
 * @param key - Local storage key.
 * @param fallback - Fallback range.
 * @returns Stored or fallback date range.
 */
function storedDateRange(key: string, fallback: DateRange): DateRange {
  try {
    const value = JSON.parse(localStorage.getItem(key) ?? 'null')

    // New shape: DateRange object with start/end strings
    if (value && typeof value === 'object' && !Array.isArray(value) && value.start && value.end) {
      return value
    }

    // Legacy shape: [start, end] array persisted by the old date picker
    if (Array.isArray(value) && value.length === 2 && value.every(item => typeof item === 'string')) {
      return { start: value[0], end: value[1] }
    }
  } catch {
    return fallback
  }

  return fallback
}

/**
 * Formats a decimal as a percentage.
 * @param value - Decimal percentage value.
 * @returns Percentage label.
 */
function formatPercent(value: number): string {
  return `${formatNumber(value * 100, 'normal', 1) ?? '0'}%`
}
</script>

<style scoped>
.group-dashboard {
  display: flex;
  flex-direction: column;
  gap: var(--s-4);
  min-height: calc(100vh - var(--s-8));
}

.group-dashboard__topbar {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: var(--s-4);
}

.group-dashboard__filters {
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: max-content;
  gap: var(--s-3);
  overflow-x: auto;
  white-space: nowrap;
}

.group-dashboard__single-filter {
  display: flex;
  min-height: 2.75rem;
  align-items: center;
  border: 1px solid rgba(255, 255, 255, 0.72);
  border-radius: 1rem;
  padding: 0 var(--s-4);
  background: rgba(255, 255, 255, 0.76);
  color: var(--text-secondary);
  backdrop-filter: blur(14px);
}

.group-dashboard__content {
  display: flex;
  flex-direction: column;
  gap: var(--s-4);
}

.group-dashboard :deep(.card) {
  border-radius: 2rem;
}

:deep(.group-dashboard-card) {
  height: 100%;
  border: 1px solid rgba(255, 255, 255, 0.72);
  border-radius: 2rem;
  padding: var(--s-5);
  background: rgba(255, 255, 255, 0.76);
  box-shadow: 0 24px 80px rgba(16, 24, 40, 0.07);
  backdrop-filter: blur(18px);
}

.group-dashboard-card__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: var(--s-3);
  margin-bottom: var(--s-4);
}

.group-dashboard-chart {
  min-height: 20rem;
}

:global(body.dark .group-dashboard__single-filter),
:global(body.dark .group-dashboard-card) {
  border-color: rgba(255, 255, 255, 0.08);
  background: rgba(17, 24, 39, 0.78);
}

@media (max-width: 768px) {
  .group-dashboard__topbar {
    flex-direction: column;
    align-items: stretch;
  }
}
</style>
